import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentProps,
} from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  canProject,
  type DocumentPointer,
  type GeneratedTaskDraft,
  type Job,
  type Task,
  type TaskBatchOrigin,
  type TaskGenerateSource,
  type TaskPriority,
} from '@allemnii-pulse/shared';
import { useCollection, useDocument } from '../../lib/firestore.js';
import { sortTasksByPriority } from '../../lib/schedule.js';
import { useProject } from '../projects/ProjectProvider.js';
import { api } from '../../lib/api.js';
import { Card } from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { Textarea } from '../../components/ui/Textarea.js';
import { cn } from '../../lib/utils.js';
import { Label } from '../../components/ui/Label.js';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../../components/ui/Select.js';
import { StatusBadge } from '../../components/ui/StatusBadge.js';
import { SourceBadge } from '../../components/ui/SourceBadge.js';
import { Term } from '../../components/ui/Term.js';
import { ExportMenu } from '../exports/ExportMenu.js';
import { AddTaskDialog } from './AddTaskDialog.js';
import { ImportTasksDialog } from './ImportTasksDialog.js';

function GenerateForm({
  tddAccepted,
  onGenerated,
}: {
  tddAccepted: boolean;
  onGenerated: (drafts: GeneratedTaskDraft[]) => void;
}) {
  const { projectId } = useProject();
  const [minCount, setMinCount] = useState(15);
  const [maxCount, setMaxCount] = useState(30);
  const [jobId, setJobId] = useState<string | null>(null);
  // Which button started the running job, so only that one reads "Generating…".
  const [generatingFrom, setGeneratingFrom] = useState<TaskGenerateSource | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: job } = useDocument<Job>(jobId ? `jobs/${jobId}` : null);

  useEffect(() => {
    if (job?.status === 'succeeded' && job.result) {
      onGenerated((job.result as { tasks: GeneratedTaskDraft[] }).tasks);
      setJobId(null);
      setGeneratingFrom(null);
    } else if (job?.status === 'failed') {
      setError(job.error ?? 'Generation failed');
      setJobId(null);
      setGeneratingFrom(null);
    }
  }, [job, onGenerated]);

  async function handleGenerate(from: TaskGenerateSource) {
    setBusy(true);
    setError(null);
    setGeneratingFrom(from);
    try {
      const { jobId: id } = await api.post<{ jobId: string }>(
        `/projects/${projectId}/tasks/generate`,
        {
          minCount,
          maxCount,
          from,
        },
      );
      setJobId(id);
    } catch {
      setError('Could not start generation.');
      setGeneratingFrom(null);
    } finally {
      setBusy(false);
    }
  }

  const running = busy || Boolean(jobId);

  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-6">
      <h2 className="text-sm font-semibold">
        {tddAccepted ? (
          <>
            Generate tasks from the accepted <Term id="tdd" /> or <Term id="prd" />
          </>
        ) : (
          <>
            Generate tasks from the accepted <Term id="prd" />
          </>
        )}
      </h2>
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="tasks-min-count">Minimum</Label>
          <Input
            id="tasks-min-count"
            type="number"
            min={1}
            value={minCount}
            onChange={(e) => setMinCount(Number(e.target.value))}
            className="w-24"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="tasks-max-count">Maximum</Label>
          <Input
            id="tasks-max-count"
            type="number"
            min={minCount}
            value={maxCount}
            onChange={(e) => setMaxCount(Number(e.target.value))}
            className="w-24"
          />
        </div>
        {tddAccepted && (
          <Button
            variant="default"
            disabled={running}
            onClick={() => void handleGenerate('tdd')}
          >
            {jobId && generatingFrom === 'tdd' ? 'Generating…' : 'Generate from TDD'}
          </Button>
        )}
        <Button
          variant={tddAccepted ? undefined : 'default'}
          disabled={running}
          onClick={() => void handleGenerate('prd')}
        >
          {jobId && generatingFrom === 'prd' ? 'Generating…' : 'Generate from PRD'}
        </Button>
      </div>
      {!tddAccepted && (
        <p className="text-xs text-muted">
          No <Term id="tdd" /> yet — tasks are planned straight from the PRD and won't link to
          design sections.
        </p>
      )}
      {error && <p className="text-sm text-danger">{error}</p>}
    </Card>
  );
}

/** A textarea that grows to fit its content, so long drafts never scroll. */
function AutoGrowTextarea({ className, value, ...props }: ComponentProps<typeof Textarea>) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
    };
    fit();
    // Re-fit when the column width changes and the text re-wraps.
    let width = el.offsetWidth;
    const observer = new ResizeObserver(() => {
      if (el.offsetWidth === width) return;
      width = el.offsetWidth;
      fit();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  return (
    <Textarea
      ref={ref}
      rows={1}
      value={value}
      className={cn('min-h-8 resize-none overflow-hidden px-3 py-1.5', className)}
      {...props}
    />
  );
}

/** A batch of drafts awaiting review, and where it came from. */
type PendingBatch = { drafts: GeneratedTaskDraft[]; origin: TaskBatchOrigin };

function ReviewTable({
  drafts: initial,
  origin,
  onValidated,
  onDiscard,
}: {
  drafts: GeneratedTaskDraft[];
  origin: TaskBatchOrigin;
  onValidated: () => void;
  onDiscard: () => void;
}) {
  const { projectId, role } = useProject();
  const canValidate = canProject(role, 'document.accept');
  const [drafts, setDrafts] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function update(tempKey: string, patch: Partial<GeneratedTaskDraft>) {
    setDrafts((ds) => ds.map((d) => (d.tempKey === tempKey ? { ...d, ...patch } : d)));
  }

  async function handleValidate() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/projects/${projectId}/tasks/validate`, { tasks: drafts, origin });
      onValidated();
    } catch {
      setError('Could not validate — check dependencies and try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold">
          Review {drafts.length} {origin === 'imported' ? 'imported' : 'generated'} tasks
        </h2>
        <Button variant="ghost" className="ml-auto" disabled={busy} onClick={onDiscard}>
          Discard
        </Button>
        {canValidate ? (
          <Button variant="default" disabled={busy} onClick={() => void handleValidate()}>
            {busy ? 'Validating…' : 'Validate & create'}
          </Button>
        ) : (
          <span className="text-xs text-muted">
            A project lead or owner validates the {origin === 'imported' ? 'imported' : 'generated'}{' '}
            tasks.
          </span>
        )}
      </div>
      {error && <p className="text-sm text-danger">{error}</p>}
      {/* Phones get a stacked card per draft instead (see the rows below), so no column headers. */}
      <div className="hidden grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_130px_70px] gap-3 px-1 text-xs font-medium text-muted md:grid">
        <span>Title</span>
        <span>Description</span>
        <span>Priority</span>
        <span>Est. hrs</span>
      </div>
      <div className="flex flex-col divide-y divide-border">
        {drafts.map((d, i) => (
          <motion.div
            key={d.tempKey}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, delay: i * 0.02 }}
            className="grid grid-cols-2 items-start gap-2 py-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_130px_70px] md:gap-3 md:py-2"
          >
            <AutoGrowTextarea
              value={d.title}
              onChange={(e) => update(d.tempKey, { title: e.target.value })}
              className="col-span-2 text-sm md:col-span-1"
            />
            <AutoGrowTextarea
              value={d.description}
              onChange={(e) => update(d.tempKey, { description: e.target.value })}
              placeholder="Short description"
              className="col-span-2 text-xs md:col-span-1"
            />
            <Select
              value={d.priority}
              onValueChange={(v) => update(d.tempKey, { priority: v as TaskPriority })}
            >
              <SelectTrigger aria-label="Priority" className="h-10 text-xs md:h-8 md:w-auto">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1.5">
              <Input
                type="number"
                aria-label="Estimated hours"
                value={d.estimateHours ?? ''}
                onChange={(e) =>
                  update(d.tempKey, { estimateHours: e.target.value ? Number(e.target.value) : null })
                }
                placeholder="hrs"
                className="h-10 min-w-0 text-xs md:h-8 md:w-16"
              />
              {/* No column header on phones, so the unit rides along with the value. */}
              <span className="text-xs text-muted md:hidden">hrs</span>
            </div>
          </motion.div>
        ))}
      </div>
    </Card>
  );
}

export function TasksPage() {
  const { projectId, project } = useProject();
  const { data: prdPointer } = useDocument<DocumentPointer>(`projects/${projectId}/documents/prd`);
  const { data: tddPointer } = useDocument<DocumentPointer>(`projects/${projectId}/documents/tdd`);
  const { data: tasks, loading } = useCollection<Task>(`projects/${projectId}/tasks`);
  const [pending, setPending] = useState<PendingBatch | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const prdAccepted = prdPointer?.status === 'accepted';
  const tddAccepted = tddPointer?.status === 'accepted';
  const onGenerated = useCallback(
    (drafts: GeneratedTaskDraft[]) => setPending({ drafts, origin: 'generated' }),
    [],
  );

  if (loading) return <div className="p-4 text-sm text-muted sm:p-6">Loading…</div>;

  if (pending) {
    return (
      <div className="mx-auto max-w-4xl p-4 sm:p-6">
        <ReviewTable
          drafts={pending.drafts}
          origin={pending.origin}
          onValidated={() => setPending(null)}
          onDiscard={() => setPending(null)}
        />
      </div>
    );
  }

  const addTaskDialog = (
    <>
      <AddTaskDialog
        projectId={projectId}
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
      />
      <ImportTasksDialog
        projectId={projectId}
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        onImported={(drafts) => setPending({ drafts, origin: 'imported' })}
      />
    </>
  );

  if (tasks.length === 0) {
    return (
      <div className="mx-auto flex max-w-lg flex-col gap-4 p-4 sm:p-6">
        {project?.projectType === 'training' ? (
          <Card className="p-6 text-center text-sm text-muted sm:p-8">
            Training projects have no PRD to generate tasks from — import a task list you already
            have, or add tasks by hand below.
          </Card>
        ) : prdAccepted ? (
          <GenerateForm tddAccepted={tddAccepted} onGenerated={onGenerated} />
        ) : (
          <Card className="p-6 text-center text-sm text-muted sm:p-8">
            Accept the <Term id="prd" /> to generate tasks automatically, import a task list you
            already have, or add one by hand below.
          </Card>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Button onClick={() => setShowImportDialog(true)}>Import tasks</Button>
          <Button variant="default" onClick={() => setShowAddDialog(true)}>
            Add task
          </Button>
        </div>
        {addTaskDialog}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-lg font-semibold">Tasks ({tasks.length})</h1>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button onClick={() => setShowImportDialog(true)}>Import tasks</Button>
          <Button onClick={() => setShowAddDialog(true)}>Add task</Button>
          <ExportMenu projectId={projectId} docType="tasks" />
        </div>
      </div>
      <Card className="flex flex-col divide-y divide-border">
        {sortTasksByPriority(tasks).map((t) => (
          <Link
            key={t.id}
            to={`/projects/${projectId}/tasks/${t.id}`}
            className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3 hover:bg-surface-2 md:flex-nowrap md:py-2.5"
          >
            {/* Full-width on phones so the badges drop beneath it rather than crushing it. */}
            <span className="min-w-0 basis-full break-words text-sm md:flex-1">{t.title}</span>
            <SourceBadge source={t.source} />
            <StatusBadge status={t.status} />
          </Link>
        ))}
      </Card>
      {addTaskDialog}
    </div>
  );
}
